/**
 * OrdoTopBar — the canonical top bar that sits above the page content
 * on every authenticated route.
 *
 * The centered pill is a plain navigation search (`OrdoSearch`) wired
 * to the user's profile nav — the Ordo agent (chat / "ask Agent" /
 * suggested-question chips / the purple ✦ sparkle) has been removed
 * from the customer-facing app for now. Search + "Jump to" navigation
 * stay: the pill, ⌘K, and the mobile search icon all open the same
 * `OrdoSearch` palette.
 *
 * Layout (mirrors the mockup):
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  ☰  [🔍 Search…                     ⌘K]        🔔  👤        │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * The avatar's full menu (theme / sign out) lives in the sidebar's
 * footer per Plan 03 mockup feedback #5. The topbar carries the inbox
 * bell only — wired to `TodayTasksBell`, which surfaces the user's unread
 * notifications, tasks due by today and onboarding comments.
 */
import { AppTopBar, AppTopBarHamburger, AppTopBarLeft, AppTopBarRight } from '@ordo/ui/app-topbar'
import { Search } from 'lucide-react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { TodayTasksBell } from '@/components/notifications/today-tasks-bell'
import { OrdoSearch, useSearchOpen } from '@/components/ordo-search'
import { usePageTitle } from '@/stores/page-title'

export function OrdoTopBar(): React.ReactElement {
  const intl = useIntl()
  const openSearch = useSearchOpen((s) => s.setOpen)
  const pageTitle = usePageTitle((s) => s.title)

  return (
    <AppTopBar size="md" sticky>
      <AppTopBarLeft className="min-w-0">
        <AppTopBarHamburger aria-label={intl.formatMessage({ id: 'topbar.menu' })} />
        {/* Consolidated mobile bar: page title sits next to the hamburger.
            Hidden on desktop, where the page renders its own header title. */}
        {pageTitle ? (
          <span className="truncate text-lg font-medium tracking-tight text-foreground md:hidden">{pageTitle}</span>
        ) : null}
      </AppTopBarLeft>

      {/* Desktop: the centered search pill. Hidden on mobile but kept
          mounted (`md:contents`) so ⌘K + the palette stay available — the
          mobile search icon opens the same palette. */}
      <div className="hidden flex-1 md:contents">
        <OrdoSearch />
      </div>

      <AppTopBarRight>
        {/* Mobile-only search icon → opens the same search palette. */}
        <button
          type="button"
          aria-label={intl.formatMessage({ id: 'topbar.search' })}
          onClick={() => openSearch(true)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-muted hover:text-foreground md:hidden">
          <Search className="h-5 w-5" />
        </button>
        <TodayTasksBell label={intl.formatMessage({ id: 'topbar.inbox' })} />
      </AppTopBarRight>
    </AppTopBar>
  )
}
